// @ts-strict-ignore
import * as React from 'react';
import { RootState } from '../reducers/reducers';
import { useDispatch, useSelector } from 'react-redux';
import {
    AllRegressionStatuses,
    AvailableBuild,
    BuildStatus,
    ChecklistItemPlacement,
    CIBuildSelectionReason,
    RegressionStatus,
    RegressionTestingStrategy,
    ReleaseStepRegressionTestingData,
    RunwayResponseType,
} from '../types';
import { getError, getIsLoading, getUpdated } from '../helpers';
import styles from './styles/stepStyles';
import { AppSettingsUpdatable, ReleaseUpdatable } from '../actions/updatable';
import { tryUpdateRelease, UpdateRegressionStatusRequestReleaseStep } from '../actions/release';
import { FeatureSection } from '../typescriptHelpers';
import { compact, uniqBy } from 'lodash';
import { antGrey, runwayColors } from './styles/globalStyles';
import { ReleaseStepInactiveContext } from '../contexts';
import Text from 'antd/lib/typography/Text';
import Layout from 'antd/lib/layout/layout';
import Space from 'antd/lib/space';
import Form from 'antd/lib/form';
import FormItem from 'antd/lib/form/FormItem';
import Cascader from 'antd/lib/cascader';
import Button from 'antd/lib/button';
import RegressionTestingHistory from './regressionTestingModules/regressionTestingHistory';
import RegressionTestingActiveTestRuns from './regressionTestingModules/RegressionTestingActiveTestRuns';
import CIAppStoreBuildComponent from './CIAppStoreBuildComponent';
import { RunwayUserAction } from '../helpers/aclHelpers';
import {
    selectAppTimeFormatSetting,
    selectEffectiveTimezone,
    selectIsReleaseFromScraper,
    selectRelease,
    selectReleaseStep,
    selectStepRun,
    selectUserCanPerformAction,
} from '../selectors/selectors';
import StepChecklistContainer from './StepChecklistContainer';
import { ReleaseEventsTimelineSection } from './releaseOverview/ReleaseTimelineSection';
import { ReleaseTimelineSectionContext } from './timelineEvents/EventsTimeline';
import { globalMessage } from '../utils/globalMessage';
import { EmptyStateWrapper } from './releaseEmptyState/EmptyStateWrapper';
import { ReleaseStepOrStepRunComponentProps } from '@/components/ValidatedRouteComponents';
import { useAppSettings, useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { StepRunUpdatable } from '@/actions/stepRun';
import { ToolOutlined } from '@ant-design/icons';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import RegressionTestingSettings from './RegressionTestingSettings';
import { StepConfigUpdatable, UpdateRegressionTestingStrategyRequest } from '@/actions/stepConfig';
import { tryUpdateAppSettings } from '@/actions/appSettings';
import { RegressionTestingStepSettings } from '@/types/stepConfig';

export const StepContentRegressionTesting = ({
    orgId,
    appId,
    releaseId,
    releaseStepType,
    stepRunComponentProps,
}: ReleaseStepOrStepRunComponentProps) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const isStepInactive = React.useContext(ReleaseStepInactiveContext);

    const release = useSelector((state: RootState) => selectRelease(state, releaseId));
    const isReleaseFromScraper = useSelector((state: RootState) => selectIsReleaseFromScraper(state, releaseId));
    const releaseStep = useSelector((state: RootState) => selectReleaseStep(state, releaseId, releaseStepType));
    const stepRunId = stepRunComponentProps?.stepRunId;
    const stepRun = useSelector((state: RootState) => selectStepRun(state, stepRunId));
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, appId));
    const appSettings = useAppSettings({ appId, orgId });
    const stepData: ReleaseStepRegressionTestingData =
        stepRun ? { ...stepRun.data.summaryData, ...stepRun.data.complementaryData } : releaseStep.data;
    const status = stepData.status;
    const stepConfigSettings = stepRun?.stepConfigSnapshot?.settings as RegressionTestingStepSettings;
    const strategy =
        stepConfigSettings?.regressionTestingStrategy ||
        appSettings?.regressionTestingStrategy ||
        RegressionTestingStrategy.ManualUpdatesOnly;
    const regressionStatusOptions = AllRegressionStatuses;
    const regressionStatusString = regressionStatusDisplayString(status, false);
    const availableBuilds = stepData.availableBuilds;
    const activeBuild = stepData.selectedStagingBuild;
    const isActiveBuildLocked = activeBuild?.selectedReason === CIBuildSelectionReason.ManualSelection;

    const [
        updateRegressionStatusStepRun,
        updateRegressionStatusIsLoadingStepRun,
        updateRegressionStatusResponseTypeStepRun,
        updateRegressionStatusErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.UpdateRegressionStatus, stepRunComponentProps);

    const updateRegressionStatusIsLoadingReleaseStep = useSelector((state: RootState) =>
        getIsLoading(ReleaseUpdatable.RegressionStatus, state, releaseId),
    );
    const updateRegressionStatusUpdatedReleaseStep = useSelector((state: RootState) =>
        getUpdated(ReleaseUpdatable.RegressionStatus, state, releaseId),
    );
    const updateRegressionStatusFailed = useSelector((state: RootState) =>
        getError(ReleaseUpdatable.RegressionStatus, state, releaseId),
    );

    const [regressionTestingSettingsVisible, setRegressionTestingSettingsVisible] = React.useState(false);

    const statusIsUpdating =
        stepRun ? updateRegressionStatusIsLoadingStepRun : updateRegressionStatusIsLoadingReleaseStep;

    const statusUpdated =
        stepRun ?
            updateRegressionStatusResponseTypeStepRun === RunwayResponseType.Success
        :   updateRegressionStatusUpdatedReleaseStep;

    const statusUpdateFailed = stepRun ? updateRegressionStatusErrorStepRun : updateRegressionStatusFailed;

    const testRuns = stepData.testRuns;
    const hasTestRuns = testRuns && testRuns.length > 0;
    const activeTestRuns = [];
    const pastTestRuns = [];

    testRuns?.forEach((testRun) => {
        if (testRun.isHistorical) {
            pastTestRuns.push(testRun);
        } else {
            activeTestRuns.push(testRun);
        }
    });

    const userId = useSelector((state: RootState) => state.user.id);
    const regressionBuildInfo = stepData.regressionBuildInfo;

    const currentTestRunsPercentComplete =
        stepData.nonHistoricalTestRunsCompletionFraction ?
            Math.round(stepData.nonHistoricalTestRunsCompletionFraction * 100)
        :   null;

    const testItems = stepData.testItems;
    const buildHasPassedRegressionTesting = status === RegressionStatus.Passed;
    const shouldDisableOverallStatus = [
        RegressionTestingStrategy.DisableOverallStatus,
        RegressionTestingStrategy.DisableOverallStatusWithCLIs,
        RegressionTestingStrategy.StatusBasedOnItems,
        RegressionTestingStrategy.StatusBasedOnItemsWithCLIs,
    ].includes(strategy);

    const showNewerBuildAvailable =
        stepData.regressionBuildInfo &&
        activeBuild &&
        stepData.regressionBuildInfo?.ciBuild.buildHash !== activeBuild?.ciBuild.buildHash &&
        activeBuild.selectedReason !== CIBuildSelectionReason.ManualSelection &&
        activeBuild?.ciBuild.buildStatus === BuildStatus.Success;

    const userCanUpdateRegressionStatus = useSelector((state: RootState) =>
        selectUserCanPerformAction(state, RunwayUserAction.UAUpdateRegressionStatus, release.id),
    );

    const buildLabelComponent = React.useCallback(
        (labelString: string, build: AvailableBuild, disabled: boolean) => {
            const isActiveBuild = build.hash === activeBuild?.ciBuild?.buildHash;
            const activeText = '(active build)';

            return (
                <Space
                    style={{
                        alignItems: 'stretch',
                        ...(disabled && {
                            cursor: 'not-allowed',
                            opacity: 0.2,
                            backgroundColor: antGrey.grey2,
                        }),
                    }}
                >
                    <Text>{labelString}</Text>
                    <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <CIAppStoreBuildComponent
                            ciBuildIdentifier={build.buildIdentifier}
                            ciBuildIntegrationId={build.integrationId}
                            appStoreBuildIdentifier={build.appStoreBuildIdentifier}
                            appStoreBuildIntegrationId={build.appStoreIntegrationId}
                            isAppStoreBuildProcessed={true} // just pass true here, no is-processing handling
                        />
                    </div>
                    {isActiveBuild && <Text>{activeText}</Text>}
                </Space>
            );
        },
        [activeBuild?.ciBuild?.buildHash],
    );

    const options = React.useMemo(
        () =>
            regressionStatusOptions
                .filter((status) => status !== RegressionStatus.NotStarted)
                .map((regressionStatus) => {
                    const labelString = labelStringForRegressionStatus(regressionStatus);
                    const children = uniqBy(availableBuilds, 'hash').map((build) => {
                        const isDisabled = isActiveBuildLocked && build.hash != activeBuild?.ciBuild.buildHash;
                        const labelNode = buildLabelComponent(labelString, build, isDisabled);

                        return {
                            value: build.hash,
                            label: labelNode,
                            disabled: isDisabled,
                        };
                    });

                    const isFinalStatus =
                        regressionStatus === RegressionStatus.Failed || regressionStatus === RegressionStatus.Passed;

                    return {
                        value: regressionStatus,
                        label: regressionStatusDisplayString(regressionStatus, false),
                        children: children ?? [],
                        disabled: shouldDisableOverallStatus && isFinalStatus,
                    };
                }),
        [availableBuilds],
    );

    const defaultStatus =
        isReleaseFromScraper ? RegressionStatus.InProgress
        : status === RegressionStatus.NotStarted ? null
        : status;
    const initialValues = {
        status: (defaultStatus && compact([defaultStatus, regressionBuildInfo?.ciBuild.buildHash ?? null])) ?? null,
    };

    const saveStatus = (request: UpdateRegressionStatusRequestReleaseStep) => {
        if (stepRun) {
            return updateRegressionStatusStepRun(request);
        }

        dispatch(tryUpdateRelease(ReleaseUpdatable.RegressionStatus, request, request.releaseId));
    };

    const onStatusChange = React.useCallback(() => {
        const [status, buildHash] = form.getFieldValue('status');
        const request: UpdateRegressionStatusRequestReleaseStep = {
            appId,
            orgId,
            userId,
            releaseId,
            status,
            buildHash: buildHash ?? null,
        };

        saveStatus(request);
    }, [appId, orgId, userId, releaseId, form]);

    const resetRegressionStatus = React.useCallback(() => {
        const request: UpdateRegressionStatusRequestReleaseStep = {
            appId,
            orgId,
            userId,
            releaseId,
            status: RegressionStatus.NotStarted,
            buildHash: null,
        };

        saveStatus(request);
    }, [appId, orgId, userId, releaseId]);

    const continueTestingOnLatestBuild = React.useCallback(() => {
        const request: UpdateRegressionStatusRequestReleaseStep = {
            appId,
            orgId,
            userId,
            releaseId,
            status: RegressionStatus.InProgress,
            buildHash: activeBuild?.ciBuild.buildHash,
        };

        saveStatus(request);
    }, [appId, orgId, userId, releaseId, activeBuild?.ciBuild.buildHash]);

    const subtitleText = React.useCallback(
        (isReleaseFromScraper: boolean) => {
            return subtitleTextForStatus(status, isReleaseFromScraper, regressionBuildInfo?.ciBuild.buildIdentifier);
        },
        [status, regressionBuildInfo?.ciBuild.buildIdentifier],
    );

    const updateStatusField = () => {
        const newStatus =
            isReleaseFromScraper ? RegressionStatus.InProgress
            : status === RegressionStatus.NotStarted ? null
            : status;
        const newCascaderValue = compact([newStatus, regressionBuildInfo?.ciBuild.buildHash ?? null]);
        form.setFieldValue('status', newCascaderValue);
    };

    // TODO: RUN-14235 - Remove release step actions
    const releaseStepRegressionStategyIsUpdating = useIsLoading(AppSettingsUpdatable.RegressionTestingStrategy);
    const releaseStepRegressionStategyUpdated = useUpdated(AppSettingsUpdatable.RegressionTestingStrategy);
    const releaseStepRegressionStategyUpdateError = useError(AppSettingsUpdatable.RegressionTestingStrategy);

    const [
        updateRegressionTestingStrategy,
        updateRegressionTestingStrategyIsLoading,
        updateRegressionTestingStrategyResponseType,
        updateRegressionTestingStrategyError,
    ] = useCallUpdatable(StepConfigUpdatable.UpdateRegressionTestingStrategy, stepRunComponentProps);

    const saveRegressionTestingStrategy = (input: UpdateRegressionTestingStrategyRequest) => {
        if (stepRunComponentProps) {
            updateRegressionTestingStrategy(input);
        } else {
            dispatch(tryUpdateAppSettings(AppSettingsUpdatable.RegressionTestingStrategy, input));
        }
    };

    const strategyIsUpdating =
        stepRunComponentProps ? updateRegressionTestingStrategyIsLoading : releaseStepRegressionStategyIsUpdating;
    const strategyUpdated =
        stepRunComponentProps ?
            updateRegressionTestingStrategyResponseType === RunwayResponseType.Success
        :   releaseStepRegressionStategyUpdated;
    const strategyUpdateError =
        stepRunComponentProps ? updateRegressionTestingStrategyError : releaseStepRegressionStategyUpdateError;

    React.useEffect(() => {
        if (strategyUpdated) {
            globalMessage.success('Regression testing handling updated!');
        }
    }, [strategyUpdated]);

    React.useEffect(() => {
        if (strategyUpdateError) {
            globalMessage.error('Could not update regression testing handling.');
        }
    }, [strategyUpdateError]);

    React.useEffect(() => {
        if (statusUpdated) {
            globalMessage.success('Regression testing status updated!');
        }
    }, [statusUpdated]);

    React.useEffect(() => {
        if (statusUpdateFailed) {
            globalMessage.error(statusUpdateFailed);
        }
    }, [statusUpdated]);

    React.useEffect(() => {
        updateStatusField();
    }, [status, regressionBuildInfo?.ciBuild.buildHash]);

    return (
        <Layout
            style={{
                ...styles.innerContent,
                paddingTop: 0,
            }}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'stretch',
                }}
            >
                <EmptyStateWrapper
                    releaseStepType={releaseStepType}
                    appId={appId}
                    releaseId={releaseId}
                    featureSections={[]}
                    shouldForceDisable
                >
                    {(isReleaseFromScraper) => {
                        return (
                            <Space direction="vertical" size={4} style={{ paddingTop: 28 }}>
                                <Text
                                    style={{
                                        fontSize: 30,
                                        fontWeight: 600,
                                        color: tagColorForRegressionStatus(status, isReleaseFromScraper),
                                    }}
                                >
                                    {isReleaseFromScraper ? 'Passed' : regressionStatusString}
                                </Text>
                                <Text type="secondary">{subtitleText(isReleaseFromScraper)}</Text>
                            </Space>
                        );
                    }}
                </EmptyStateWrapper>
                <ReleaseEventsTimelineSection
                    orgId={orgId}
                    appId={appId}
                    releaseId={releaseId}
                    stepRunId={stepRunComponentProps?.stepRunId}
                    context={ReleaseTimelineSectionContext.ReleaseStep}
                    releaseStepType={releaseStepType}
                />
            </div>
            <EmptyStateWrapper
                releaseStepType={releaseStepType}
                appId={appId}
                featureSections={[FeatureSection.RegressionTestingStatusPicker]}
                releaseId={releaseId}
                shouldShowAsTooltip
            >
                <div
                    style={{
                        ...styles.spaceBetween,
                        flexGrow: 1,
                        flexShrink: 0,
                        background: antGrey.grey2,
                        padding: 34,
                        borderRadius: 10,
                        marginBottom: 34,
                        marginTop: 34,
                    }}
                >
                    <div style={{ alignSelf: 'flex-end' }}>
                        <PermissionedActionButton
                            actionType={RunwayUserAction.UAUpdateAppSettings}
                            type="link"
                            icon={<ToolOutlined />}
                            disabled={strategyIsUpdating}
                            onClick={() => setRegressionTestingSettingsVisible(true)}
                        >
                            Build testing status settings
                        </PermissionedActionButton>
                    </div>
                    <div
                        style={{
                            width: '70%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <div style={{ width: '100%', paddingTop: 24 }}>
                            <Form
                                form={form}
                                wrapperCol={{ span: 24 }}
                                initialValues={initialValues}
                                onValuesChange={onStatusChange}
                                layout="vertical"
                            >
                                <FormItem label="Build testing status" name="status">
                                    <Cascader
                                        loading={statusIsUpdating}
                                        size="large"
                                        allowClear={false}
                                        options={options}
                                        expandTrigger="hover"
                                        disabled={
                                            (buildHasPassedRegressionTesting && shouldDisableOverallStatus) ||
                                            availableBuilds.length === 0 ||
                                            !userCanUpdateRegressionStatus
                                        }
                                        popupClassName="cascader-regression"
                                        displayRender={(_, selectedOptions) => {
                                            if (selectedOptions.length === 0) {
                                                return null;
                                            }

                                            const selectedFirst = selectedOptions[0];
                                            const selectedSecond = selectedOptions[1];

                                            return (
                                                <Space style={{ alignItems: 'stretch' }}>
                                                    <Text>{selectedFirst?.label} /</Text>
                                                    {selectedSecond?.label}
                                                </Space>
                                            );
                                        }}
                                        placeholder="Select"
                                    />
                                </FormItem>
                            </Form>
                            {showNewerBuildAvailable && (
                                <Space direction="vertical">
                                    <Text type="warning">
                                        A newer build{' '}
                                        {activeBuild ? '(#' + activeBuild?.ciBuild.buildIdentifier + ') ' : ''}
                                        is available.
                                    </Text>
                                    <Space direction="horizontal">
                                        <Button onClick={resetRegressionStatus} disabled={isStepInactive}>
                                            Reset
                                        </Button>
                                        <Button onClick={continueTestingOnLatestBuild} disabled={isStepInactive}>
                                            Continue testing on latest build
                                        </Button>
                                    </Space>
                                </Space>
                            )}
                        </div>
                    </div>
                </div>
            </EmptyStateWrapper>
            {hasTestRuns && activeTestRuns.length > 0 && (
                <RegressionTestingActiveTestRuns
                    testRuns={activeTestRuns}
                    currentTestRunsPercentComplete={currentTestRunsPercentComplete}
                    appId={appId}
                />
            )}
            <StepChecklistContainer
                stepRunComponentProps={stepRunComponentProps}
                orgId={orgId}
                appId={appId}
                releaseId={releaseId}
                releaseStepType={releaseStepType}
                items={testItems ?? []}
                placement={ChecklistItemPlacement.RegressionTesting}
            />
            {hasTestRuns && (
                <RegressionTestingHistory
                    tests={pastTestRuns}
                    use24HourTimeFormat={use24HourTimeFormat}
                    effectiveTimezone={effectiveTimezone}
                />
            )}
            <RegressionTestingSettings
                open={regressionTestingSettingsVisible}
                isLoading={strategyIsUpdating}
                isWorkflow={!!stepRun}
                currentRegressionStrategy={strategy}
                onChangeStrategy={(updatedStrategy) =>
                    saveRegressionTestingStrategy({
                        regressionTestingStrategy: updatedStrategy,
                        appId,
                        orgId,
                    })
                }
                onCancel={() => setRegressionTestingSettingsVisible(false)}
            />
        </Layout>
    );
};

export const regressionStatusDisplayString = (status: RegressionStatus, isReleaseFromScraper: boolean) => {
    if (isReleaseFromScraper) {
        return 'Passed';
    }
    switch (status) {
        case RegressionStatus.NotStarted:
            return 'Not started';
        case RegressionStatus.InProgress:
            return 'In progress';
        case RegressionStatus.Failed:
            return 'Failed';
        case RegressionStatus.Passed:
            return 'Passed';
        default:
            return 'Not started';
    }
};

const tagColorForRegressionStatus = (status: RegressionStatus, isReleaseFromScraper: boolean) => {
    if (isReleaseFromScraper) {
        return runwayColors.statusGrey;
    }
    switch (status) {
        case RegressionStatus.NotStarted:
            return runwayColors.statusGrey;
        case RegressionStatus.InProgress:
            return runwayColors.statusYellow;
        case RegressionStatus.Failed:
            return runwayColors.statusRed;
        case RegressionStatus.Passed:
            return runwayColors.statusGreen;
    }
};

const subtitleTextForStatus = (status: RegressionStatus, isReleaseFromScraper: boolean, buildIdentifier?: string) => {
    if (isReleaseFromScraper) {
        return 'Regression testing complete.';
    }

    const buildIdentifierString = buildIdentifier ? ` on build #${buildIdentifier}.` : '.';
    switch (status) {
        case RegressionStatus.Failed:
            return `Regression testing has failed${buildIdentifierString}`;
        case RegressionStatus.Passed:
            return `Regression testing passed${buildIdentifierString}`;
        case RegressionStatus.InProgress:
            return `Regression testing in progress${buildIdentifierString}`;
        case RegressionStatus.NotStarted:
            return 'Regression testing has not started.';
        default:
            return null;
    }
};

const labelStringForRegressionStatus = (status: RegressionStatus) => {
    return status === RegressionStatus.InProgress ? 'Testing on: ' : 'Tested on: ';
};
